<script lang="ts">
	import { onDestroy } from 'svelte';
	import { createThumbnailResolver } from 'src/components/Camera/helpers/encryption/recording-image-decryption';
	import { real_id } from 'src/helpers/interface_helpers';
	import { isSingleCameraLiveFeedEnabled } from 'src/components/Dashboards/dash-camera-helpers';
	import type { DashCameraWidgetConfig } from 'src/components/Dashboards/dash-types';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import DashCameraDeviceCard from './DashCameraDeviceCard.svelte';

	type AdditionalText = {
		value: string;
		icon?: string;
		type?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
	};

	export let variant: 'button' | 'quick-control' = 'button';
	export let cameraConfig: DashCameraWidgetConfig | undefined;
	export let value: string;
	export let additionalText: AdditionalText | undefined;
	export let editMode = false;
	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	export let offline = false;
	export let selected = false;
	export let showFullscreen = false;

	let cameraPreviewImage = '';

	// Memoized resolver keeps the current image until the new one resolves
	// (decryption is async), then swaps and revokes the replaced blob URL.
	const previewResolver = createThumbnailResolver((url) => (cameraPreviewImage = url));

	$: useLiveFeed = !editMode && isSingleCameraLiveFeedEnabled(device?.id, cameraConfig);
	$: if (device?.id)
		previewResolver.update({ deviceId: real_id(String(device.id)), previewSize: 'large' });

	onDestroy(previewResolver.destroy);

	$: isQuickControl = variant === 'quick-control';
	$: ariaLabel = isQuickControl ? 'Dash Camera Parameter Widget' : 'Dash Camera Button Widget';
	$: arrowRight = !editMode;
</script>

<DashCameraDeviceCard
	aria-label={ariaLabel}
	isFocusable={!editMode}
	{value}
	{additionalText}
	previewImage={cameraPreviewImage}
	imageFit="cover"
	{useLiveFeed}
	showLiveIndicator={useLiveFeed && !offline}
	{offline}
	{selected}
	{editMode}
	{arrowRight}
	{showFullscreen}
	hideRightTitleSlot={isQuickControl}
	{device}
	{status}
	{settings}
	useCloudConnectionIcon
	on:click
	on:arrow-click
	on:fullscreen
	on:handle-drag-start
	on:handle-drag-end
/>
